import { type OverviewDatum } from '@resistapp/client/data-utils/plot-data/build-overview-line-data';
import { FullSample, getOriginalSamplingPointId } from '@resistapp/common/types';
import { chain } from 'lodash';
import { useMemo } from 'react';
import { useSampleDataContext } from '../../sample-data-context';

interface UseSingleSiteSelectedProps {
  supportedSamples: FullSample[] | undefined;
  mapData: OverviewDatum[] | undefined;
  enabled?: boolean;
}

export function useSelectedSiteDatum({
  supportedSamples,
  mapData,
  enabled = true,
}: UseSingleSiteSelectedProps): OverviewDatum | undefined {
  const { queryFilters } = useSampleDataContext();

  const { selectedSamplingPointIdsOrdered } = queryFilters.filters;
  const selectedSiteDatum = useMemo(() => {
    if (!enabled) {
      return undefined;
    }
    if (mapData && mapData.length === 1) {
      return mapData[0];
    }
    // Find samples with selected sampling point IDs and group by location
    const uniqSelectedLocations = chain(supportedSamples)
      .filter(s => {
        const envId = getOriginalSamplingPointId(s.samplingPoint) || s.samplingPoint.id;
        return selectedSamplingPointIdsOrdered.includes(envId);
      })
      .map(s => `${s.samplingPoint.lat},${s.samplingPoint.lon}`)
      .uniq()
      .value();

    // Single site is selected if we have 1 sampling point selected or 2 sampling points at the same location
    const singleSiteSelected =
      selectedSamplingPointIdsOrdered.length === 1 ||
      (selectedSamplingPointIdsOrdered.length === 2 && uniqSelectedLocations.length === 1);

    // If single site selected, use the sampling point IDs for filtering map data
    const selectedSmplPntIds = singleSiteSelected ? selectedSamplingPointIdsOrdered : [];

    return selectedSmplPntIds.length
      ? mapData?.filter(d => {
          // Normally, we have both the before and after sampling points in selectedSamplingPointIdsOrdered,
          // but the implicit population of selectedSamplingPointIdsOrdered
          // for a single process-site project with a treated WW selected only ends up selecting one of the site sampling points.
          const primarySmplPntId = getOriginalSamplingPointId(d.samplingPoint) || d.samplingPoint.id;
          const pairedSmplPntId = d.samplingPointAfter
            ? getOriginalSamplingPointId(d.samplingPointAfter) || d.samplingPointAfter.id
            : undefined;
          return (
            selectedSmplPntIds.includes(primarySmplPntId) ||
            (!!pairedSmplPntId && selectedSmplPntIds.includes(pairedSmplPntId))
          );
        })[0]
      : undefined;
  }, [enabled, mapData, supportedSamples, selectedSamplingPointIdsOrdered]);

  return selectedSiteDatum;
}
